import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CalendarDays, UserCheck, Users, ClipboardCheck, Coins, TrendingUp,
  Download, RotateCcw, ChevronRight, Percent,
} from "lucide-react";
import { api, apiError, API, fmtDate } from "../../lib/api";
import { localeOf } from "../../i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const TABS = ["events", "ambassadors", "teams", "partners", "campaigns"];

const Kpi = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="vy-card p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-2 font-display text-3xl font-extrabold tabular-nums">{value ?? 0}</p>
    {hint != null && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const Bar = ({ pct }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
    <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

function Trend({ rows, label }) {
  const max = Math.max(1, ...rows.map((r) => r.checked_in));
  return (
    <section className="vy-card p-5" data-testid="analytics-trend">
      <h2 className="font-display text-lg font-bold">{label}</h2>
      <div className="mt-5 flex h-40 items-end gap-2 overflow-x-auto">
        {rows.map((r) => (
          <div key={r.month} className="flex min-w-[44px] max-w-[96px] flex-1 flex-col items-center gap-2"
            data-testid={`trend-bar-${r.month}`}>
            <span className="text-xs font-bold tabular-nums">{r.checked_in}</span>
            <div className="w-full rounded-t-lg bg-primary/85 transition-all hover:bg-accent"
              style={{ height: `${Math.max(6, (r.checked_in / max) * 110)}px` }} />
            <span className="text-[10px] font-semibold text-muted-foreground">{r.month?.slice(2)}</span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
      </div>
    </section>
  );
}

export default function Analytics() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", campaign_id: "", partner_id: "", team_id: "" });
  const [opts, setOpts] = useState({ campaigns: [], partners: [], teams: [] });
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("events");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  useEffect(() => {
    Promise.all([api.get("/campaigns"), api.get("/partners"), api.get("/teams")])
      .then(([c, p, tm]) => setOpts({ campaigns: c.data, partners: p.data, teams: tm.data }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setBusy(true);
    Promise.all([
      api.get(`/analytics/overview?${query}`),
      api.get(`/analytics/${tab}?${query}`),
    ]).then(([o, r]) => { setOverview(o.data); setRows(r.data); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(false));
  }, [query, tab]);

  const k = overview?.kpi || {};
  const exportCsv = () => {
    window.open(`${API}/analytics/export/${tab}?${query}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{t("analytics.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("analytics.lead")}</p>
        </div>
        <Button data-testid="analytics-export-btn" onClick={exportCsv}
          className="h-11 rounded-full font-bold">
          <Download className="mr-2 h-4 w-4" />{t("analytics.export")}
        </Button>
      </div>

      <section className="vy-card grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-6" data-testid="analytics-filters">
        <label className="min-w-0 text-xs font-bold uppercase text-muted-foreground">
          {t("common.start")}
          <Input data-testid="filter-date-from" type="date" className="mt-1 h-11 w-full" value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        </label>
        <label className="min-w-0 text-xs font-bold uppercase text-muted-foreground">
          {t("common.end")}
          <Input data-testid="filter-date-to" type="date" className="mt-1 h-11 w-full" value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        </label>
        {[
          { key: "campaign_id", label: t("events.campaign"), list: opts.campaigns, testId: "filter-campaign" },
          { key: "partner_id", label: t("events.partner"), list: opts.partners, testId: "filter-partner" },
          { key: "team_id", label: t("admin.team"), list: opts.teams, testId: "filter-team" },
        ].map((f) => (
          <label key={f.key} className="min-w-0 text-xs font-bold uppercase text-muted-foreground">
            {f.label}
            <select data-testid={f.testId} value={filters[f.key]}
              onChange={(e) => setFilters({ ...filters, [f.key]: e.target.value })}
              className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm font-semibold text-foreground">
              <option value="">{t("analytics.all")}</option>
              {f.list.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        ))}
        <Button variant="outline" data-testid="filter-reset-btn"
          onClick={() => setFilters({ date_from: "", date_to: "", campaign_id: "", partner_id: "", team_id: "" })}
          className="h-11 self-end rounded-full font-bold">
          <RotateCcw className="mr-2 h-4 w-4" />{t("analytics.reset")}
        </Button>
      </section>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi icon={CalendarDays} label={t("analytics.events")} value={k.events} testId="kpi-events"
          hint={`${k.upcoming || 0} ${t("events.upcoming").toLowerCase()}`} />
        <Kpi icon={Users} label={t("analytics.rsvps")} value={k.rsvps} testId="kpi-rsvps"
          hint={`${k.unique_ambassadors || 0} ${t("analytics.uniqueAmbassadors")}`} />
        <Kpi icon={UserCheck} label={t("events.checkedIn")} value={k.checked_in} testId="kpi-checked-in"
          hint={`${k.attendance_rate || 0}% ${t("analytics.attendanceRate")}`} />
        <Kpi icon={Percent} label={t("analytics.noShowRate")} value={`${k.no_show_rate || 0}%`} testId="kpi-no-show-rate"
          hint={`${k.no_shows || 0} ${t("profile.noShows").toLowerCase()}`} />
        <Kpi icon={ClipboardCheck} label={t("analytics.approvedProofs")} value={k.approved} testId="kpi-approved"
          hint={`${k.approval_rate || 0}% · ${k.pending || 0} ${t("home.pending").toLowerCase()}`} />
        <Kpi icon={Coins} label={t("home.score")} value={k.points} testId="kpi-points" />
        <Kpi icon={TrendingUp} label={t("analytics.activeAmbassadors")} value={k.active_ambassadors} testId="kpi-active" />
        <Kpi icon={ClipboardCheck} label={t("events.missions")} value={k.missions} testId="kpi-missions" />
      </div>

      <Trend rows={overview?.trend || []} label={t("analytics.trend")} />

      <section className="vy-card p-5" data-testid="analytics-top-ambassadors">
        <h2 className="font-display text-lg font-bold">{t("analytics.topAmbassadors")}</h2>
        <ul className="mt-3 divide-y divide-border">
          {(overview?.top_ambassadors || []).map((a, i) => (
            <li key={a.user_id} className="flex items-center gap-4 py-3" data-testid={`top-ambassador-${a.user_id}`}>
              <span className="font-display text-lg font-extrabold text-muted-foreground">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <Link to={`/staff/ambassadors/${a.user_id}`} className="font-semibold hover:text-accent">{a.name}</Link>
                <p className="text-xs text-muted-foreground">{a.team_name || "—"} · {a.checked_in} {t("home.attended").toLowerCase()}</p>
              </div>
              <span className="font-display text-lg font-extrabold tabular-nums">{a.points}</span>
            </li>
          ))}
          {(overview?.top_ambassadors || []).length === 0 && (
            <li className="py-3 text-sm text-muted-foreground">{t("common.none")}</li>
          )}
        </ul>
      </section>

      <div className="flex flex-wrap gap-2" data-testid="analytics-tabs">
        {TABS.map((tb) => (
          <button key={tb} data-testid={`analytics-tab-${tb}`} onClick={() => setTab(tb)}
            className={`h-11 rounded-full px-5 text-sm font-bold transition-colors ${
              tab === tb ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}>
            {t(`analytics.by.${tb}`)}
          </button>
        ))}
      </div>

      <section className="vy-card overflow-x-auto p-5" data-testid={`analytics-table-${tab}`}>
        {busy && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t(`analytics.by.${tab}`)}</th>
              {tab === "events" && <th className="py-2">{t("events.partner")}</th>}
              {tab === "ambassadors" && <th className="py-2">{t("admin.team")}</th>}
              {tab !== "events" && <th className="py-2">{t("analytics.events")}</th>}
              <th className="py-2">{t("analytics.rsvps")}</th>
              <th className="py-2">{t("events.checkedIn")}</th>
              <th className="py-2">{t("analytics.attendanceRate")}</th>
              <th className="py-2">{t("analytics.approvedProofs")}</th>
              <th className="py-2">{t("home.score")}</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const id = r.event_id || r.user_id || r.team_id || r.partner_id || r.campaign_id || idx;
              return (
                <tr key={id} className="border-t border-border" data-testid={`analytics-row-${id}`}>
                  <td className="py-3 pr-4">
                    <p className="font-semibold">{r.title || r.name || t("common.none")}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.starts_at ? fmtDate(r.starts_at, locale) : (r.email || r.region || r.category || r.objective || "—")}                    </p>
                  </td>
                  {tab === "events" && <td className="py-3 text-muted-foreground">{r.partner_name || "—"}</td>}
                  {tab === "ambassadors" && <td className="py-3 text-muted-foreground">{r.team_name || "—"}</td>}
                  {tab !== "events" && <td className="py-3 tabular-nums">{r.events}</td>}
                  <td className="py-3 tabular-nums">{r.rsvps}</td>
                  <td className="py-3 tabular-nums">{r.checked_in}</td>
                  <td className="w-32 py-3">
                    <span className="text-xs font-bold tabular-nums">{r.attendance_rate}%</span>
                    <Bar pct={r.attendance_rate} />
                  </td>
                  <td className="py-3 tabular-nums">{r.approved}</td>
                  <td className="py-3 font-bold tabular-nums">{r.points}</td>
                  <td className="py-3 text-right">
                    {tab === "ambassadors" && (
                      <Link to={`/staff/ambassadors/${r.user_id}`} data-testid={`analytics-open-${r.user_id}`}
                        className="inline-flex items-center text-xs font-bold text-accent">
                        {t("analytics.viewHistory")}<ChevronRight className="h-4 w-4" />
                      </Link>
                    )}
                    {tab === "events" && (
                      <Link to={`/staff/events/${r.event_id}`} className="inline-flex items-center text-xs font-bold text-accent">
                        {t("events.detail")}<ChevronRight className="h-4 w-4" />
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
            {!busy && rows.length === 0 && (
              <tr><td colSpan={9} className="py-4 text-sm text-muted-foreground">{t("common.none")}</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
